/**
 * Contact Intelligence Engine
 * 
 * Scores relationships based on weighted criteria:
 * - Relationship Strength: 35% (recency × frequency × depth × sentiment)
 * - Strategic Relevance: 25% (ICP fit + timing)
 * - Influence Reach: 15% (seniority + network centrality)
 * - Responsiveness: 15% (time-to-response + reliability)
 * - Reciprocity Momentum: 10% (give↔get balance)
 */

import { blink } from '@/lib/blink';
import { contactTimelineService } from './contactTimelineService';
import { requestManager } from '@/lib/requestManager';

export interface CIScoreBreakdown {
  relationshipStrength: number; // 0-100
  strategicRelevance: number;   // 0-100
  influenceReach: number;        // 0-100
  responsiveness: number;        // 0-100
  reciprocityMomentum: number;   // 0-100
  totalScore: number;            // Weighted composite 0-100
  tier: 'A' | 'B' | 'C' | 'D';   // Priority tier
  whyNow: string[];              // Array of current factors
  nextBestAction: string;        // Recommended next step
}

export interface ContactCard {
  contactId: string;
  email: string;
  name?: string;
  company?: string;
  ciScore: CIScoreBreakdown;
  lastTouch: string | null;
  nextTouchWindow: string;
  openLoops: string[];
  quickNotes: string;
}

export interface OrgCard {
  orgName: string;
  contacts: string[]; // Contact IDs
  multiThreadCoverage: number; // Percentage
  championMap: Record<string, string>; // Contact ID -> role
  influenceWeb: Array<{ from: string; to: string; strength: number }>;
  riskAreas: string[];
}

export interface OpportunityThread {
  id: string;
  orgName: string;
  contacts: string[];
  stitchedComms: Array<{
    date: string;
    type: 'email' | 'meeting';
    summary: string;
  }>;
  decisionTimeline: string;
  blockers: string[];
  unlocks: string[];
}

export interface Top50Dashboard {
  relationships: ContactCard[];
  weeklyTouchPlan: Array<{
    contactId: string;
    action: string;
    priority: number;
  }>;
  powerNodes: ContactCard[];
  decayingRelationships: ContactCard[];
}

export class ContactIntelligenceEngine {
  /**
   * Calculate comprehensive CI score for a contact
   */
  async calculateCIScore(
    userId: string,
    contactId: string
  ): Promise<CIScoreBreakdown> {
    try {
      // Fetch contact data
      const contacts = await requestManager.execute(
        `contact:${contactId}`,
        () => blink.db.contacts.list({
          where: { id: contactId, userId },
          limit: 1
        }),
        { priority: 8, cacheTTL: 60000 }
      );

      if (contacts.length === 0) {
        throw new Error('Contact not found');
      }

      const contact = contacts[0];

      // Fetch enrichment data
      const enrichments = await requestManager.execute(
        `enrichment:${contactId}`,
        () => blink.db.emailContactEnrichment.list({
          where: { contactId, userId },
          limit: 1
        }),
        { priority: 8, cacheTTL: 60000 }
      );

      const enrichment = enrichments[0];

      // Load timeline for depth analysis
      const timeline = await contactTimelineService.loadTimelineForContact(
        userId,
        contact.email
      );

      // Calculate individual scores
      const relationshipStrength = await this.calculateRelationshipStrength(
        contact,
        enrichment,
        timeline
      );

      const strategicRelevance = await this.calculateStrategicRelevance(
        contact,
        enrichment
      );

      const influenceReach = await this.calculateInfluenceReach(
        contact,
        enrichment
      );

      const responsiveness = await this.calculateResponsiveness(
        contact,
        enrichment,
        timeline
      );

      const reciprocityMomentum = await this.calculateReciprocityMomentum(
        contact,
        enrichment,
        timeline
      );

      // Calculate weighted total score
      const totalScore = Math.round(
        relationshipStrength * 0.35 +
        strategicRelevance * 0.25 +
        influenceReach * 0.15 +
        responsiveness * 0.15 +
        reciprocityMomentum * 0.10
      );

      // Determine tier
      const tier = this.determineTier(totalScore);

      // Generate why-now factors
      const whyNow = await this.generateWhyNowFactors(
        contact,
        enrichment,
        timeline,
        totalScore
      );

      // Generate next best action
      const nextBestAction = await this.generateNextBestAction(
        contact,
        enrichment,
        timeline,
        totalScore
      );

      return {
        relationshipStrength,
        strategicRelevance,
        influenceReach,
        responsiveness,
        reciprocityMomentum,
        totalScore,
        tier,
        whyNow,
        nextBestAction
      };
    } catch (error) {
      console.error('Failed to calculate CI score:', error);
      throw error;
    }
  }

  /**
   * Calculate Relationship Strength (35%)
   * = recency × frequency × depth × sentiment
   */
  private async calculateRelationshipStrength(
    contact: any,
    enrichment: any,
    timeline: any[]
  ): Promise<number> {
    let score = 0;

    // Recency (0-25 points)
    const lastInteraction = contact.lastInteractionAt
      ? new Date(contact.lastInteractionAt)
      : null;
    
    if (lastInteraction) {
      const daysAgo = Math.floor(
        (Date.now() - lastInteraction.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysAgo <= 7) score += 25;
      else if (daysAgo <= 30) score += 20;
      else if (daysAgo <= 90) score += 15;
      else if (daysAgo <= 180) score += 10;
      else score += 5;
    }

    // Frequency (0-25 points)
    const totalInteractions = parseInt(contact.totalInteractions) || 0;
    if (totalInteractions >= 50) score += 25;
    else if (totalInteractions >= 20) score += 20;
    else if (totalInteractions >= 10) score += 15;
    else if (totalInteractions >= 5) score += 10;
    else score += Math.min(totalInteractions * 2, 5);

    // Depth (0-25 points) - based on timeline events
    const emails = timeline.filter(e => e.type === 'email');
    const meetings = timeline.filter(e => e.type === 'meeting');
    
    score += Math.min(emails.length * 0.5, 15);
    score += Math.min(meetings.length * 2, 10);

    // Sentiment (0-25 points)
    const positiveEvents = timeline.filter(e => e.sentiment === 'positive').length;
    const negativeEvents = timeline.filter(e => e.sentiment === 'negative').length;
    const sentimentRatio = timeline.length > 0
      ? (positiveEvents - negativeEvents) / timeline.length
      : 0;
    
    score += Math.max(0, Math.min(sentimentRatio * 25 + 12.5, 25));

    return Math.min(Math.round(score), 100);
  }

  /**
   * Calculate Strategic Relevance (25%)
   * = ICP fit + timing (initiative alignment, budget, urgency)
   */
  private async calculateStrategicRelevance(
    contact: any,
    enrichment: any
  ): Promise<number> {
    let score = 50; // Base score

    // ICP fit (0-50 points)
    // Check relationship type and stage
    if (contact.relationshipType === 'investor' || 
        contact.relationshipType === 'strategic_partner') {
      score += 20;
    } else if (contact.relationshipType === 'customer' || 
               contact.relationshipType === 'prospect') {
      score += 15;
    }

    // Relationship stage
    if (contact.relationshipStage === 'active' || 
        contact.relationshipStage === 'engaged') {
      score += 15;
    } else if (contact.relationshipStage === 'growing') {
      score += 10;
    }

    // Engagement level
    if (contact.engagementLevel === 'high') {
      score += 15;
    } else if (contact.engagementLevel === 'medium') {
      score += 10;
    }

    // Timing (0-50 points)
    // Recent activity indicates current relevance
    const lastInteraction = contact.lastInteractionAt
      ? new Date(contact.lastInteractionAt)
      : null;
    
    if (lastInteraction) {
      const daysAgo = Math.floor(
        (Date.now() - lastInteraction.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysAgo <= 3) score += 25;
      else if (daysAgo <= 7) score += 20;
      else if (daysAgo <= 14) score += 15;
      else if (daysAgo <= 30) score += 10;
      else score += 5;
    }

    return Math.min(Math.round(score), 100);
  }

  /**
   * Calculate Influence Reach (15%)
   * = seniority + network centrality + bridge potential
   */
  private async calculateInfluenceReach(
    contact: any,
    enrichment: any
  ): Promise<number> {
    let score = 30; // Base score

    // Seniority indicators (0-40 points)
    const company = contact.company?.toLowerCase() || '';
    const name = contact.name?.toLowerCase() || '';
    
    // Check for executive titles
    const seniorityKeywords = [
      'ceo', 'cto', 'cfo', 'coo', 'founder', 'partner',
      'vp', 'vice president', 'director', 'head of', 'chief'
    ];
    
    if (seniorityKeywords.some(keyword => name.includes(keyword))) {
      score += 30;
    } else if (name.includes('senior') || name.includes('lead')) {
      score += 20;
    } else if (name.includes('manager')) {
      score += 10;
    }

    // Company size/reputation (0-30 points)
    // Check if contact is from a well-known company
    const majorCompanies = [
      'google', 'microsoft', 'amazon', 'meta', 'apple',
      'salesforce', 'oracle', 'ibm', 'adobe', 'intel'
    ];
    
    if (majorCompanies.some(comp => company.includes(comp))) {
      score += 20;
    }

    // Network centrality (0-30 points)
    // Based on number of interactions and domains
    const totalSent = parseInt(enrichment?.totalSent || '0');
    const totalReceived = parseInt(enrichment?.totalReceived || '0');
    const totalComms = totalSent + totalReceived;
    
    if (totalComms >= 20) score += 20;
    else if (totalComms >= 10) score += 15;
    else if (totalComms >= 5) score += 10;
    else score += totalComms;

    return Math.min(Math.round(score), 100);
  }

  /**
   * Calculate Responsiveness (15%)
   * = time-to-first-response + reliability
   */
  private async calculateResponsiveness(
    contact: any,
    enrichment: any,
    timeline: any[]
  ): Promise<number> {
    let score = 50; // Base score

    // Average response time (0-50 points)
    const avgResponseTime = parseFloat(enrichment?.avgResponseTimeHours || '0');
    
    if (avgResponseTime > 0) {
      if (avgResponseTime <= 4) score += 30;
      else if (avgResponseTime <= 24) score += 25;
      else if (avgResponseTime <= 48) score += 20;
      else if (avgResponseTime <= 72) score += 15;
      else score += 10;
    } else {
      score += 15; // No data, assume moderate
    }

    // Reliability (0-50 points)
    // Based on consistency of interactions
    const totalSent = parseInt(enrichment?.totalSent || '0');
    const totalReceived = parseInt(enrichment?.totalReceived || '0');
    
    if (totalSent > 0 && totalReceived > 0) {
      const responseRate = totalReceived / totalSent;
      score += Math.min(Math.round(responseRate * 30), 30);
    }

    // Recent activity bonus
    const recentEmails = timeline.filter(e => {
      const eventDate = new Date(e.date);
      const daysAgo = (Date.now() - eventDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 30 && e.type === 'email';
    });
    
    if (recentEmails.length >= 5) score += 20;
    else score += recentEmails.length * 4;

    return Math.min(Math.round(score), 100);
  }

  /**
   * Calculate Reciprocity Momentum (10%)
   * = give↔get balance + intros made + value exchange
   */
  private async calculateReciprocityMomentum(
    contact: any,
    enrichment: any,
    timeline: any[]
  ): Promise<number> {
    let score = 40; // Base score

    // Give-get balance (0-40 points)
    const totalSent = parseInt(enrichment?.totalSent || '0');
    const totalReceived = parseInt(enrichment?.totalReceived || '0');
    
    if (totalSent > 0 && totalReceived > 0) {
      const balance = Math.min(totalSent, totalReceived) / 
                      Math.max(totalSent, totalReceived);
      score += Math.round(balance * 30);
    }

    // Meeting frequency (0-30 points)
    const meetings = timeline.filter(e => e.type === 'meeting');
    score += Math.min(meetings.length * 3, 30);

    // Engagement trend (0-30 points)
    const recentEvents = timeline.filter(e => {
      const eventDate = new Date(e.date);
      const daysAgo = (Date.now() - eventDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 30;
    });
    
    const olderEvents = timeline.filter(e => {
      const eventDate = new Date(e.date);
      const daysAgo = (Date.now() - eventDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo > 30 && daysAgo <= 90;
    });
    
    if (recentEvents.length > olderEvents.length) {
      score += 30; // Momentum is increasing
    } else if (recentEvents.length === olderEvents.length) {
      score += 15; // Stable momentum
    }

    return Math.min(Math.round(score), 100);
  }

  /**
   * Determine priority tier from total score
   */
  private determineTier(totalScore: number): 'A' | 'B' | 'C' | 'D' {
    if (totalScore >= 80) return 'A';
    if (totalScore >= 60) return 'B';
    if (totalScore >= 40) return 'C';
    return 'D';
  }

  /**
   * Generate why-now factors
   */
  private async generateWhyNowFactors(
    contact: any,
    enrichment: any,
    timeline: any[],
    totalScore: number
  ): Promise<string[]> {
    const factors: string[] = [];

    // Check recency
    const lastInteraction = contact.lastInteractionAt
      ? new Date(contact.lastInteractionAt)
      : null;
    
    if (lastInteraction) {
      const daysAgo = Math.floor(
        (Date.now() - lastInteraction.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysAgo <= 7) {
        factors.push('Recently active (last 7 days)');
      } else if (daysAgo > 60) {
        factors.push('Relationship cooling - needs attention');
      }
    }

    // Check engagement
    if (contact.engagementLevel === 'high') {
      factors.push('High engagement level');
    }

    // Check relationship health
    if (contact.relationshipHealth === 'excellent') {
      factors.push('Excellent relationship health');
    } else if (contact.relationshipHealth === 'poor') {
      factors.push('Poor relationship health - risk area');
    }

    // Check communication patterns
    const recentMeetings = timeline.filter(e => {
      const eventDate = new Date(e.date);
      const daysAgo = (Date.now() - eventDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 14 && e.type === 'meeting';
    });
    
    if (recentMeetings.length > 0) {
      factors.push(`${recentMeetings.length} recent meeting(s)`);
    }

    // Check for strategic indicators
    if (contact.relationshipType === 'investor') {
      factors.push('Strategic investor relationship');
    } else if (contact.relationshipType === 'strategic_partner') {
      factors.push('Strategic partnership opportunity');
    }

    // Default if no factors
    if (factors.length === 0) {
      factors.push('Steady relationship - maintain cadence');
    }

    return factors;
  }

  /**
   * Generate next best action
   */
  private async generateNextBestAction(
    contact: any,
    enrichment: any,
    timeline: any[],
    totalScore: number
  ): Promise<string> {
    const lastInteraction = contact.lastInteractionAt
      ? new Date(contact.lastInteractionAt)
      : null;
    
    if (!lastInteraction) {
      return 'Send initial outreach email to establish contact';
    }

    const daysAgo = Math.floor(
      (Date.now() - lastInteraction.getTime()) / (1000 * 60 * 60 * 24)
    );

    // High-value contacts (Tier A)
    if (totalScore >= 80) {
      if (daysAgo > 14) {
        return 'Schedule coffee/video call to reconnect (2 min)';
      } else if (daysAgo > 7) {
        return 'Send quick check-in email with value add (2 min)';
      } else {
        return 'Share relevant article or intro opportunity (3 min)';
      }
    }

    // Medium-value contacts (Tier B)
    if (totalScore >= 60) {
      if (daysAgo > 30) {
        return 'Send catch-up email with life/work update (3 min)';
      } else if (daysAgo > 14) {
        return 'Comment on their recent social post (1 min)';
      } else {
        return 'Maintain current cadence - monitor for opportunities';
      }
    }

    // Lower-value contacts (Tier C/D)
    if (daysAgo > 90) {
      return 'Consider archiving or re-qualifying this relationship';
    } else if (daysAgo > 60) {
      return 'Send low-effort touch: like social post or forward article';
    } else {
      return 'Maintain current cadence - opportunistic outreach only';
    }
  }

  /**
   * Generate Contact Card with full CI data
   */
  async generateContactCard(
    userId: string,
    contactId: string
  ): Promise<ContactCard> {
    const contacts = await requestManager.execute(
      `contact:${contactId}`,
      () => blink.db.contacts.list({
        where: { id: contactId, userId },
        limit: 1
      }),
      { priority: 8, cacheTTL: 60000 }
    );

    if (contacts.length === 0) {
      throw new Error('Contact not found');
    }

    const contact = contacts[0];
    const ciScore = await this.calculateCIScore(userId, contactId);

    // Calculate next touch window
    const lastTouch = contact.lastInteractionAt || null;
    const nextTouchWindow = this.calculateNextTouchWindow(lastTouch, ciScore.tier);

    return {
      contactId: contact.id,
      email: contact.email,
      name: contact.name,
      company: contact.company,
      ciScore,
      lastTouch,
      nextTouchWindow,
      openLoops: [], // TODO: Extract from notes/emails
      quickNotes: contact.relationshipType || ''
    };
  }

  /**
   * Generate Top 50 dashboard
   */
  async generateTop50Dashboard(userId: string): Promise<Top50Dashboard> {
    // Fetch all contacts
    const contacts = await requestManager.execute(
      `contacts:${userId}:top100`,
      () => blink.db.contacts.list({
        where: { userId },
        orderBy: { aiRelationshipScore: 'desc' },
        limit: 100 // Get more to filter
      }),
      { priority: 5, cacheTTL: 120000 }
    );

    // Calculate CI scores for all contacts
    const contactCards: ContactCard[] = [];
    
    for (const contact of contacts) {
      try {
        const card = await this.generateContactCard(userId, contact.id);
        contactCards.push(card);
      } catch (error) {
        console.error(`Failed to generate card for ${contact.email}:`, error);
      }
    }

    // Sort by CI score
    contactCards.sort((a, b) => b.ciScore.totalScore - a.ciScore.totalScore);

    // Take top 50
    const top50 = contactCards.slice(0, 50);

    // Identify power nodes (Tier A with high influence)
    const powerNodes = top50.filter(
      card => card.ciScore.tier === 'A' && card.ciScore.influenceReach >= 70
    );

    // Identify decaying relationships (high score but no recent activity)
    const decayingRelationships = top50.filter(card => {
      if (!card.lastTouch) return false;
      const daysAgo = Math.floor(
        (Date.now() - new Date(card.lastTouch).getTime()) / (1000 * 60 * 60 * 24)
      );
      return card.ciScore.totalScore >= 70 && daysAgo > 30;
    });

    // Generate weekly touch plan (top 10 actions)
    const weeklyTouchPlan = top50
      .filter(card => card.ciScore.tier === 'A' || card.ciScore.tier === 'B')
      .slice(0, 10)
      .map((card, index) => ({
        contactId: card.contactId,
        action: card.ciScore.nextBestAction,
        priority: index + 1
      }));

    return {
      relationships: top50,
      weeklyTouchPlan,
      powerNodes,
      decayingRelationships
    };
  }

  /**
   * Calculate next touch window based on last touch and tier
   */
  private calculateNextTouchWindow(
    lastTouch: string | null,
    tier: 'A' | 'B' | 'C' | 'D'
  ): string {
    if (!lastTouch) {
      return 'Immediately';
    }

    const lastTouchDate = new Date(lastTouch);
    const now = new Date();
    
    // Recommended cadence by tier
    const cadence = {
      A: 14,  // Every 2 weeks
      B: 30,  // Every month
      C: 60,  // Every 2 months
      D: 90   // Every quarter
    };

    const daysToAdd = cadence[tier];
    const nextTouch = new Date(lastTouchDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);

    if (nextTouch <= now) {
      return 'Overdue - reach out now';
    }

    const daysUntil = Math.floor(
      (nextTouch.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntil === 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil <= 7) return `In ${daysUntil} days`;
    if (daysUntil <= 14) return `In ${Math.round(daysUntil / 7)} week(s)`;
    return `In ${Math.round(daysUntil / 30)} month(s)`;
  }

  /**
   * Update contact CI score in database
   */
  async updateContactCIScore(userId: string, contactId: string): Promise<void> {
    try {
      const ciScore = await this.calculateCIScore(userId, contactId);

      // Update or create contact_intelligence record
      const existingIntel = await blink.db.contactIntelligence.list({
        where: { contactId, userId },
        limit: 1
      });

      const intelligenceData = {
        analysisType: 'ci_score',
        relationshipAnalysis: JSON.stringify(ciScore),
        confidenceScore: ciScore.totalScore,
        updatedAt: new Date().toISOString()
      };

      if (existingIntel.length > 0) {
        await blink.db.contactIntelligence.update(
          existingIntel[0].id,
          intelligenceData
        );
      } else {
        await blink.db.contactIntelligence.create({
          id: `intel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          contactId,
          userId,
          ...intelligenceData,
          createdAt: new Date().toISOString()
        });
      }

      // Update contact's AI relationship score
      await blink.db.contacts.update(contactId, {
        aiRelationshipScore: ciScore.totalScore,
        relationshipHealth: this.scoreToHealth(ciScore.totalScore),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to update CI score:', error);
      throw error;
    }
  }

  /**
   * Convert CI score to relationship health
   */
  private scoreToHealth(score: number): string {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'poor';
  }

  /**
   * Batch update CI scores for all user contacts
   */
  async batchUpdateCIScores(userId: string): Promise<{
    updated: number;
    failed: number;
  }> {
    const contacts = await requestManager.execute(
      `contacts:${userId}:batch`,
      () => blink.db.contacts.list({
        where: { userId },
        limit: 500
      }),
      { priority: 3, cacheTTL: 300000 }
    );

    let updated = 0;
    let failed = 0;

    for (const contact of contacts) {
      try {
        await this.updateContactCIScore(userId, contact.id);
        updated++;
      } catch (error) {
        console.error(`Failed to update CI score for ${contact.email}:`, error);
        failed++;
      }
    }

    return { updated, failed };
  }
}

export const contactIntelligenceEngine = new ContactIntelligenceEngine();
